import * as R from 'ramda/src/index';
import qs from 'qs';
import moment from 'moment';
import {computed, action, extendObservable, decorate, toJS} from 'mobx';
import {buildFerryUrl} from 'providers/ferries/api';
import {getAccommodation, setAccommodation} from 'providers/storageAccommodation';
import {
  ONEWAY_TRIP_TYPE,
  ROUND_TRIP_TYPE,
  MULTI_TRIP_TYPE,
  carrierCode,
  getInvertType,
  findByCode,
  getAccomodationUrl,
  hierarchyPorts,
  transformQuery,
  getPopularPortsCountries
} from 'ferries/Home/components/FerrySearchMask/helperStore';
import {
  cacheKey,
  cacheKeyAccommodation,
  lowerCaseAllWordsExceptFirstLetter,
  isGreekLocale
} from 'ferries/helpers/common';
import {
  dateIsSameOrAfterToday,
  obDateIsSameOrAfterTodayAndIbDateIsAfterObDate
} from 'helpers/time';
import Fuse from 'fuse.js/dist/fuse.common';
import {onlyCharacters} from 'helpers/text';
import {addDataLayer} from 'helpers/commonHelper';
import {mobileMediaQuery, tabletMediaQuery} from 'helpers/mediaQuery';
import {setFilterCarrierCode, removeFilterCarrierCode} from 'utils/session';
import {isGreekText} from 'providers/ferries/autocompleteResultsBasedOnTypedLanguage/utils';

class FerrySearchMaskStore {
  minTrips = 2;
  maxTrips = 10;
  originalPorts = [];

  constructor({getRecentQueries, getPorts, onGetPorts, isTravelplanet24, translations}) {
    extendObservable(this, {
      isMobile: mobileMediaQuery.matches,
      isTablet: tabletMediaQuery.matches,
      product: 'ferry',
      typeTrip: ONEWAY_TRIP_TYPE, // 0 oneway, 1 roundtrip, 2 multitrip
      recentQueries: [],
      triedToSubmit: false,
      errors: [],
      loading: false,
      searchAccommodation: {isChecked: true},
      trips: [this.newTrip()],
      showMask: false
    });

    Object.assign(this, {
      isTravelplanet24,
      onGetPorts,
      translations
    });

    this.trips.push(this.newTrip()); // getting 3 days after first trip

    mobileMediaQuery.addEventListener('change', ({matches}) => this.isMobile = matches);
    tabletMediaQuery.addEventListener('change', ({matches}) => this.isTablet = matches);

    getPorts({isBilingual: true}).then((ports) => {
      this.setPorts(ports);

      const {search} = window.location;
      const tripDataFromSession = this.loadTripDataFromSession(); //load data from sessionStorage

      if (onGetPorts) {
        this.fillWithQuery(onGetPorts());
      } else if (search) {
        this.fillWithQuery(qs.parse(search.slice(1)));
      } else if (tripDataFromSession) {
        this.fillWithQuery(tripDataFromSession);
      }
    });

    getRecentQueries(cacheKey).then((queries) => {
      this.recentQueries = queries;
    });

    getAccommodation(cacheKeyAccommodation).then((value) => {
      this.searchAccommodation = {isChecked: value};
    });
  }

  fillWithQuery = ({dep, arr, obDate, ibDate, typeTrip}) => {
    const deps = dep ? dep.split(',') : [];
    const arrs = arr ? arr.split(',') : [];
    const obDates = obDate && !(obDate instanceof Date) ? obDate.split(',') : (obDate instanceof Date ? [obDate] : []);
    const ibDates = ibDate && !(ibDate instanceof Date) ? ibDate.split(',') : (ibDate instanceof Date ? [ibDate] : []);
    const nofTrips = deps.length || 1;
    const typeTripNumber = parseInt(typeTrip ?? ONEWAY_TRIP_TYPE);

    const currentTripsLength = this.noOfTrips;
    if (nofTrips > currentTripsLength) {
      for (let i = 0; i < nofTrips - currentTripsLength; i++) {
        this.addTrip();
      }
    } else if (nofTrips < currentTripsLength) {
      for (let i = currentTripsLength; i > nofTrips; i--) {
        this.removeTrip(i - 1);
      }
    }

    for (let tripIndex = 0; tripIndex < nofTrips; tripIndex++) {
      if (deps[tripIndex] && deps[tripIndex].length >= 3) {
        this.setPortByThreeLetterCode('dep', deps[tripIndex], tripIndex, undefined, isGreekLocale);
      }

      if (arrs[tripIndex] && arrs[tripIndex].length >= 3) {
        this.setPortByThreeLetterCode('arr', arrs[tripIndex], tripIndex, undefined, isGreekLocale);
      }

      if (!(obDates[tripIndex] instanceof Date)) {
        obDates[tripIndex] = new Date(obDates[tripIndex]);
      }

      if (!(ibDates[tripIndex] instanceof Date)) {
        ibDates[tripIndex] = new Date(ibDates[tripIndex]);
      }

      if (dateIsSameOrAfterToday(obDates[tripIndex])) {
        this.setDate('obDate', obDates[tripIndex], tripIndex);
      }

      if (obDateIsSameOrAfterTodayAndIbDateIsAfterObDate(obDates[tripIndex], ibDates[tripIndex])) {
        this.setDates({fromDate: obDates[tripIndex], toDate: ibDates[tripIndex]}, tripIndex);
      }
    }

    if ([ONEWAY_TRIP_TYPE, ROUND_TRIP_TYPE, MULTI_TRIP_TYPE].includes(typeTripNumber)) {
      this.setTypeTrip(typeTripNumber);
    }

  };

  portString = ({locationName, locationAbbr, locationCountry}) => {
    const portName = lowerCaseAllWordsExceptFirstLetter(locationName);
    return [`${portName}, ${locationCountry}`, `${portName} (${locationAbbr}), ${locationCountry}`];
  };

  setPort = (type, value, tripIndex) => {
    const valueIsArray = Array.isArray(value);
    const val = valueIsArray ? value[0] : onlyCharacters(value);
    const valQuery = valueIsArray ? value[1] : onlyCharacters(value);

    this.trips[tripIndex][type] = val;
    this.trips[tripIndex][type + 'Query'] = valQuery;
    this.trips[tripIndex][type + 'Suggestion'] = {};

    if (this.errors.length > 0 && this.errors[tripIndex] && this.errors[tripIndex][type]) {
      this.errors[tripIndex][type] = [];
    }
    
    this.storeTripDataInSession();
  };

  setPorts = (ports) => {
    const transformedPorts = ports.map((port) => ({
      ...port,
      isMetropolitan: port.parentLocationID === null && port.childPorts.length > 0,
      alternativeNames: port.alternativeNames.split('|'),
      filtered: true // it is used in popular ports
    }));

    this.originalPorts = R.sortBy(R.prop('locationName'), transformedPorts);

    this.trips.forEach((trip) => {
      trip.depPossiblePorts = this.originalPorts;
      trip.depSelected = this.originalPorts;
      trip.depPopularPorts = {
        ports: transformedPorts,
        filters: {countries: getPopularPortsCountries(this.originalPorts)}
      };
    });
  };

  swapPorts = (tripIndex) => {
    this.swapValues('dep', 'arr', tripIndex);
    this.swapValues('depQuery', 'arrQuery', tripIndex);
    this.swapValues('depSuggestion', 'arrSuggestion', tripIndex);
    this.swapValues('depSelected', 'arrSelected', tripIndex);
    this.swapValues('depPossiblePorts', 'arrPossiblePorts', tripIndex);
  };

  swapValues = (type1, type2, tripIndex) => {
    const type = this.trips[tripIndex][type1];
    this.trips[tripIndex][type1] = this.trips[tripIndex][type2];
    this.trips[tripIndex][type2] = type;
    this.storeTripDataInSession();
  };

  setPortByThreeLetterCode = (type, code, tripIndex, autoClick, isGreekLocation) => {
    const port = findByCode(this.originalPorts.filter((port) =>
      isGreekLocation && isGreekText(port.locationName) ||
      !isGreekLocation && !isGreekText(port.locationName)
    ), code);

    if (port) {
      this.setPort(type, this.portString(port), tripIndex);
    }
    this.onSuggestionSelected(type, port, tripIndex, autoClick);
  };

  clearSuggestions = (type, tripIndex) => {
    const invertType = getInvertType(type);
    const emptyPortsValue = !(this.trips[tripIndex][type] || this.trips[tripIndex][invertType]);
    if (emptyPortsValue && type === 'dep') {
      this.trips[tripIndex][`${invertType}Selected`] = [];
      this.trips[tripIndex][`${invertType}PopularPorts`].ports = [];
      this.trips[tripIndex][`${invertType}PopularPorts`].filters = {countries: []};
    }
  };

  fetchSuggestions = (type, value, tripIndex) => {
    const val = onlyCharacters(value);
    const trip = this.trips[tripIndex];
    const options = {
      includeMatches: true,
      threshold: 0.3,
      distance: 80,
      keys: [
        'locationAbbr',
        'locationName',
        'locationNameLatin',
        'alternativeNames'
      ]
    };
    const isDeparture = type === 'dep';

    const pattern = val.replace(/[-[\]{}()*+?,\\^$|#]/gi, '');
    // if we are on the departure always search the whole list
    // even if arrival is filled in, and in case a selection is
    // made for departure that does not belong in the dependent
    // ports then empty the arrival field
    const listOfPortsToSearch = isDeparture ?
      this.originalPorts.filter(({dependentPorts}) => dependentPorts.length > 0) :
      trip[`${type}PossiblePorts`];
    const fuse = new Fuse(toJS(listOfPortsToSearch), options);

    const fusedPorts = fuse.search(pattern).map((item) => item.item);
    const selected = hierarchyPorts({
      selectedPorts: fusedPorts,
      matchPorts: isDeparture ? this.originalPorts : fusedPorts,
      pattern
    });

    trip[`${type}Selected`] = selected;
    if (trip[`${type}Selected`].length === 0) {
      trip[`${type}Selected`] = listOfPortsToSearch;
    }
  };

  findPorts = (type, tripIndex) => {
    const invertType = getInvertType(type);
    const trip = this.trips[tripIndex];
    const suggestion = trip[`${type}Suggestion`];
    const isGreek = isGreekText(suggestion?.plaintext);

    const dependentPorts = (suggestion?.dependentPorts ?? [])
      .filter((port) => ![suggestion.parentLocationID, suggestion.ferryOwnLocationID].includes(port));

    // WE FILTER THE POSSIBLE PORTS BASED ON DEPENDENT PORTS
    const matchPorts = dependentPorts.reduce((acc, dependentPort) => {
      const foundPorts = this.originalPorts.filter(({
        ferryOwnLocationID,
        plaintext
      }) => ferryOwnLocationID === dependentPort && isGreekText(plaintext) === isGreek);

      if (foundPorts.length) {
        acc.push(...foundPorts);
      }
      return acc;
    }, []);
    const portsSortedByLocationName = R.sortBy(R.prop('locationName'), matchPorts);

    trip[`${invertType}PossiblePorts`] = hierarchyPorts({
      selectedPorts: portsSortedByLocationName,
      matchPorts: portsSortedByLocationName
    });
    trip[`${invertType}Selected`] = trip[`${invertType}PossiblePorts`];

    if (type === 'dep') {
      trip[`${invertType}PopularPorts`].ports = matchPorts;
      trip[`${invertType}PopularPorts`].filters.countries = getPopularPortsCountries(matchPorts);
    }
  };

  onSuggestionSelected = (type, suggestion, tripIndex, autoClick) => {
    this.trips[tripIndex][`${type}Suggestion`] = suggestion;

    // fill the departure of the next trip with the arrival of the previous one
    // if the departure is not filled in
    if (type === 'arr' && tripIndex < this.noOfTrips - 1 && !this.trips[tripIndex + 1][getInvertType(type)]) {
      this.trips[tripIndex + 1][getInvertType(type)] = this.trips[tripIndex][type];
      this.trips[tripIndex + 1][getInvertType(type) + 'Query'] = this.trips[tripIndex][type + 'Query'];
      this.trips[tripIndex + 1][getInvertType(type) + 'Suggestion'] = this.trips[tripIndex][type + 'Suggestion'];
      this.findPorts(getInvertType(type), tripIndex + 1);
    }
    this.findPorts(type, tripIndex);

    if (autoClick && !this.isMobile) {
      setTimeout(() => {
        if (type === 'dep') {
          document.querySelector(`[aria-controls='react-autowhatever-arr-${tripIndex}']`).click();
        } else if (type === 'arr') {
          const datepickerInput = document.getElementById(`datepicker-input-${tripIndex}`);
          datepickerInput.focus();
          datepickerInput.click();
        }
      }, 300);
    }
  };

  setDate = (type, date, tripIndex) => { // it is called in roundtrip - ibDate
    this.trips[tripIndex][type] = date;
    this.storeTripDataInSession();
  };

  setDates = (date, tripIndex) => {
    const {fromDate, toDate} = date;
    // check if new date is greater than the next's trip date and every trip after that
    this.setNextDates(tripIndex, fromDate);

    this.trips[tripIndex].obDate = fromDate;
    this.trips[tripIndex].ibDate = toDate;

    this.storeTripDataInSession();
  };

  setNextDates = (tripIndex, fromDate) => {
    let oldDate = this.trips[tripIndex].obDate;
    [tripIndex, this.noOfTrips - 1].forEach((item) => {
      const dateToAdd = item === tripIndex ? fromDate : this.trips[item].obDate;
      if (item + 1 < this.noOfTrips && this.trips[item + 1] && dateToAdd > this.trips[item + 1].obDate) {
        let diffInDays = moment(this.trips[item + 1].obDate).diff(oldDate, 'days');
        oldDate = this.trips[item + 1].obDate;
        this.trips[item + 1].obDate = moment(dateToAdd).add(diffInDays, 'days').toDate();
        this.trips[item + 1].ibDate = moment(dateToAdd).add(diffInDays, 'days').toDate();
      }
    });
  };

  setTypeTrip = (val) => {
    this.typeTrip = val;
    this.storeTripDataInSession();
  };

  setRecentSearches = (queries) => {
    this.recentQueries = queries;
  };

  onChangeSwitch = (field) => {
    field.isChecked = !field.isChecked;
  };

  onChangePopularPortCountry = (popularPorts, countryCode, selected) => {
    const countries = popularPorts.filters.countries.map((country) => ({
      ...country,
      selected: selected ? false : country.code === countryCode
    })).sort((a, b) => a.name.localeCompare(b.name));

    const selectedCountry = countries.find(({selected}) => selected);
    const countriesWithoutSel = countries.filter(({selected}) => !selected);

    popularPorts.filters.countries = [selectedCountry, ...countriesWithoutSel].filter(Boolean);

    popularPorts.ports.forEach((port) => {
      port.filtered = selected ? true : port.locationCountry === countryCode;
    });
  };

  setErrors = ({errors}) => {
    if (this.triedToSubmit && errors) {
      this.loading = false;
    }
    this.errors = errors ?? [];
  };

  submitForm = (event) => {
    event.preventDefault();
    this.triedToSubmit = true;
    this.errors = [];
    const {
      typeTrip, routes, showSwitchAccommodation,
      searchAccommodation: {isChecked}, setErrors
    } = this;
    buildFerryUrl({
      typeTrip,
      trips: routes
    })
      .then((url) => {
        const {location: {pathname, hash}} = window;
        if (url !== pathname + hash) {
          this.loading = true;

          if (carrierCode?.length === 3 && carrierCode === 'SJT') {
            setFilterCarrierCode(carrierCode);
          } else {
            removeFilterCarrierCode();
          }

          if ([ONEWAY_TRIP_TYPE, ROUND_TRIP_TYPE].includes(typeTrip)) {
            const accommodationUrl = showSwitchAccommodation && isChecked &&
              getAccomodationUrl(typeTrip, routes[0], 1);
            setAccommodation(cacheKeyAccommodation, isChecked);

            if (accommodationUrl) {
              window.open(url, '_blank');
              window.location = accommodationUrl;
            } else {
              window.location = url;
            }
          } else {
            window.location = url;
          }
        }
      })
      .then(() => {
        this.loading = false;
        addDataLayer({event: 'tripSearch'});
      })
      .catch(setErrors);
  };

  stopLoading = () => {
    this.loading = false;
  };

  newTrip = () => {
    const prevTrip = this.noOfTrips > 0 ? this.trips[this.noOfTrips - 1] : undefined;

    return {
      dep: '',
      depQuery: '',
      arr: '',
      arrQuery: '',
      depSuggestion: {},
      arrSuggestion: {},
      depSelected: [],
      arrSelected: [],
      depPossiblePorts: [],
      arrPossiblePorts: [],
      depPopularPorts: {ports: [], filters: {countries: []}},
      arrPopularPorts: {ports: [], filters: {countries: []}},
      obDate: prevTrip ? moment(prevTrip.obDate).add(3, 'days').toDate() : moment().add(1, 'days').toDate(),
      ibDate: prevTrip ? moment(prevTrip.ibDate).add(3, 'days').toDate() : moment().add(5, 'days').toDate()
    };
  };

  addTrip = () => {
    if (this.noOfTrips < this.maxTrips) {
      this.trips.push(this.newTrip());
      this.trips[this.noOfTrips - 1].depSelected = [];
      this.trips[this.noOfTrips - 1].arrSelected = [];
      this.trips[this.noOfTrips - 1].depPopularPorts = {ports: [], filters: {countries: []}};
      this.trips[this.noOfTrips - 1].arrPopularPorts = {ports: [], filters: {countries: []}};
      this.trips[this.noOfTrips - 1].depPossiblePorts = this.originalPorts;
      this.trips[this.noOfTrips - 1].arrPossiblePorts = this.originalPorts;

      if (this.trips[this.noOfTrips - 2].arr) {
        this.trips[this.noOfTrips - 1].dep = this.trips[this.noOfTrips - 2].arr;
        this.trips[this.noOfTrips - 1].depQuery = this.trips[this.noOfTrips - 2].arrQuery;
        this.trips[this.noOfTrips - 1].depSuggestion = this.trips[this.noOfTrips - 2].arrSuggestion;
        this.findPorts('dep', this.noOfTrips - 1);
      }
    }
  };

  removeTrip = (index) => {
    if (this.noOfTrips > this.minTrips) {
      this.trips.splice(index, 1);
    }
  };

  toggleMask = () => {
    this.showMask = !this.showMask;
  };
  
  storeTripDataInSession = () => {
    const queryData =  {
      trips: this.trips.map((trip) => ({
        departure: trip.depQuery,
        arrival: trip.arrQuery,
        obDate: trip.obDate,
        ibDate: trip.ibDate
      })),
      typeTrip: this.typeTrip
    };

    const storedData = transformQuery(queryData);
    sessionStorage.setItem('queryData', JSON.stringify(storedData));
  };

  loadTripDataFromSession = () => {
    const tripsDataJSON = sessionStorage.getItem('queryData');
    return tripsDataJSON ?
      JSON.parse(tripsDataJSON) : undefined;
  };
  
  get showSwitchAccommodation() {
    const {isMobile, isTravelplanet24} = this;
    return !isMobile && isTravelplanet24;
  }

  get noOfTrips() {
    return this.trips?.length;
  }

  get routes() {
    const {typeTrip, trips} = this;
    return trips.reduce((acc, {depQuery, depSuggestion, arrQuery, arrSuggestion, obDate, ibDate}) => {

      if ([ONEWAY_TRIP_TYPE, ROUND_TRIP_TYPE].includes(typeTrip) && acc.length === 0) {
        acc.push({
          dep: depQuery,
          arr: arrQuery,
          depSuggestion,
          arrSuggestion,
          obDate,
          ibDate
        });

        if (typeTrip === ROUND_TRIP_TYPE) {
          acc.push({
            dep: arrQuery,
            arr: depQuery,
            depSuggestion,
            arrSuggestion,
            obDate: ibDate,
            ibDate
          });
        }
      } else if (typeTrip === MULTI_TRIP_TYPE) {
        acc.push({
          dep: depQuery,
          arr: arrQuery,
          depSuggestion,
          arrSuggestion,
          obDate,
          ibDate
        });
      }

      return acc;
    }, []);
  }

  get transformedTrips() {
    const {typeTrip, trips} = this;
    return [ONEWAY_TRIP_TYPE, ROUND_TRIP_TYPE].includes(typeTrip) ? [trips[0]] : trips;
  }
}

decorate(FerrySearchMaskStore, {
  findPorts: action,
  fillWithQuery: action,
  setPort: action,
  setPorts: action,
  swapValues: action,
  setPortByThreeLetterCode: action,
  clearSuggestions: action,
  fetchSuggestions: action,
  onSuggestionSelected: action,
  setDate: action,
  setDates: action,
  setTypeTrip: action,
  setRecentSearches: action,
  onChangeSwitch: action,
  onChangePopularPortCountry: action,
  setErrors: action,
  submitForm: action,
  stopLoading: action,
  toggleMask: action,
  showSwitchAccommodation: computed,
  noOfTrips: computed,
  routes: computed
});

export default FerrySearchMaskStore;
